/**
 * Exotel Call Control Ribbon - Widget Entry Point
 * 
 * This file creates a global API that can be embedded in any website/CRM
 * Usage:
 *   <script src="https://cdn.yourcompany.com/ribbon/v1/ribbon.js"></script>
 *   <script>
 *     IntalksAICallRibbon.init({ apiKey: 'xxx', position: 'bottom' });
 *   </script>
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import CallControlRibbon from './CallControlRibbon';
import { normalizeUserFacingMessage, userFacingApiMessage } from './userFacingMessages';

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

class IntalksAICallRibbonWidget {
  constructor() {
    this.root = null;
    this.container = null;
    this.config = null;
    this.credentials = null;
    this.customerData = null;
  }

  /**
   * Initialize the ribbon widget
   * @param {Object} config - Configuration object
   * @param {string} config.apiKey - Client's API key (provided by you)
   * @param {string} config.agentUserId - Agent's unique user ID (REQUIRED - used as Exotel userId for SIP registration)
   * @param {string} config.clientName - Client/company name (optional but recommended for analytics)
   * @param {string} config.position - Ribbon position: 'top', 'bottom', or 'floating'
   * @param {Function} config.onCallEvent - Callback for call events
   * @param {Function} config.onReady - Callback when ribbon is ready
   * @param {string} config.apiUrl - Your API URL (optional, defaults to production)
   */
  async init(config) {
    console.log('[IntalksAICallRibbon] 🚀 INIT called with config:', config);
    
    if (!config.apiKey) {
      console.error('[IntalksAICallRibbon] ❌ API key is required');
      return;
    }

    this.config = config;
    this.agentUserId = config.agentUserId || null;
    this.clientName = config.clientName || null;

    try {
      console.log('[IntalksAICallRibbon] 📡 Fetching credentials from API...');
      // Fetch Exotel credentials from your backend API
      const apiUrl = config.apiUrl || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                                        ? 'https://api.intalksai.com' 
                                        : 'https://api.intalksai.com');
      
      // Store API URL globally for transcription service
      window.INTALKSAI_API_URL = apiUrl;
      
      console.log('[IntalksAICallRibbon] 📡 API URL:', apiUrl);
      console.log('[IntalksAICallRibbon] 📡 Request body:', {
        apiKey: config.apiKey,
        domain: window.location.hostname,
        agentUserId: config.agentUserId || null,
        clientName: config.clientName || null
      });
      
      const response = await fetch(`${apiUrl}/api/ribbon/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey: config.apiKey,
          domain: window.location.hostname,
          agentUserId: config.agentUserId || null,
          clientName: config.clientName || null
        })
      });
      
      console.log('[IntalksAICallRibbon] 📡 API Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const friendlyError = userFacingApiMessage(
          response.status,
          errorData,
          'Could not start call service. Please try again.'
        );
        console.error('[IntalksAICallRibbon] ❌ API request failed:', response.status, response.statusText);
        console.error('[IntalksAICallRibbon] Error details:', errorData);
        throw new Error(friendlyError);
      }

      this.credentials = await response.json();
      
      // Validate credentials were received
      if (!this.credentials || !this.credentials.exotelToken || !this.credentials.userId) {
        console.error('[IntalksAICallRibbon] ❌ Invalid credentials received from API');
        console.error('[IntalksAICallRibbon] Response:', this.credentials);
        throw new Error('Calling could not be set up. Please sign in again.');
      }
      console.log('====================================================================');
      console.log('[IntalksAICallRibbon] ✅ Credentials loaded successfully');
      console.log('====================================================================');
      if (this.credentials.clientInfo) {
        console.log('[IntalksAICallRibbon] Client Info:');
        console.log('   Client Name:', this.credentials.clientInfo.clientName);
        console.log('   Client ID:', this.credentials.clientInfo.clientId);
        console.log('   Loaded from:', this.credentials.clientInfo.loadedFrom);
      }
      console.log('[IntalksAICallRibbon] Exotel User ID:', this.credentials.userId);
      console.log('[IntalksAICallRibbon] Virtual Number:', this.credentials.virtualNumber || 'N/A');
      console.log('====================================================================');

      // Create or get container
      this.container = document.getElementById('intalksai-call-ribbon-container');
      if (!this.container) {
        this.container = document.createElement('div');
        this.container.id = 'intalksai-call-ribbon-container';
        document.body.appendChild(this.container);
      }

      // Render the ribbon
      console.log('[IntalksAICallRibbon] 🎨 Calling render()...');
      this.render();
      console.log('[IntalksAICallRibbon] ✅ render() completed');

      // Call ready callback
      if (config.onReady) {
        console.log('[IntalksAICallRibbon] 📞 Calling onReady callback...');
        config.onReady();
      }

    } catch (error) {
      console.error('====================================================================');
      console.error('[IntalksAICallRibbon] ❌ Initialization failed:', error.message);
      console.error('[IntalksAICallRibbon] Error details:', error);
      console.error('====================================================================');
      this.emitRibbonNotification(
        normalizeUserFacingMessage(error.message || '') || 'Could not start call service. Please try again.',
        'error'
      );
      
      // Create or get container for error display
      this.container = document.getElementById('intalksai-call-ribbon-container');
      if (!this.container) {
        this.container = document.createElement('div');
        this.container.id = 'intalksai-call-ribbon-container';
        document.body.appendChild(this.container);
      }

      const errorMessage = escapeHtml(
        normalizeUserFacingMessage(error.message || '') || 'Could not start call service. Please try again.'
      );
      this.container.innerHTML = `
        <div style="padding: 15px; background: #fee; border: 2px solid #fcc; border-radius: 8px; color: #c00; margin: 10px;">
          <strong>Calling could not start</strong><br>
          <span style="font-size: 12px;">${errorMessage}</span><br>
          <span style="font-size: 11px; color: #666; margin-top: 5px; display: block;">
            Try refreshing the page. If this continues, contact your administrator.
          </span>
        </div>
      `;

      // Call ready callback if provided (even on error)
      if (config.onReady) {
        config.onReady();
      }
    }
  }

  /**
   * Update customer data
   * @param {Object} customerData - Customer information
   * @param {string} customerData.phoneNumber - Customer's phone number
   * @param {string} customerData.name - Customer's name
   * @param {string} customerData.email - Customer's email
   * @param {string} customerData.customerId - Your CRM's customer ID
   */
  setCustomer(customerData) {
    this.customerData = customerData;
    this.render();
  }

  /**
   * Update ribbon position
   * @param {string} position - 'top', 'bottom', or 'floating'
   */
  setPosition(position) {
    if (this.config) {
      this.config.position = position;
      this.render();
    }
  }

  /**
   * Show or hide the ribbon
   * @param {boolean} visible - Show (true) or hide (false)
   */
  setVisible(visible) {
    if (this.container) {
      this.container.style.display = visible ? 'block' : 'none';
    }
  }

  /**
   * Minimize or expand the ribbon
   * @param {boolean} minimized - Minimized (true) or expanded (false)
   */
  setMinimized(minimized) {
    if (this.config) {
      this.config.minimized = minimized;
      this.render();
    }
  }

  /**
   * Make a call programmatically
   * @param {string} phoneNumber - Phone number to dial
   */
  makeCall(phoneNumber) {
    // This will be handled by the ribbon component
    // We can add a ref or event system if needed
    console.log('[IntalksAICallRibbon] Making call to:', phoneNumber);
  }

  /**
   * Render the ribbon component
   */
  render() {
    console.log('[IntalksAICallRibbon] 🎨 RENDER called');
    console.log('[IntalksAICallRibbon] Container exists:', !!this.container);
    console.log('[IntalksAICallRibbon] Credentials exist:', !!this.credentials);
    
    if (!this.container) {
      console.error('[IntalksAICallRibbon] ❌ Container not found - cannot render');
      return;
    }
    
    // Validate credentials
    if (!this.credentials || !this.credentials.exotelToken || !this.credentials.userId) {
      console.error('[IntalksAICallRibbon] ❌ Missing credentials');
      console.error('[IntalksAICallRibbon] Credentials object:', this.credentials);
      this.container.innerHTML =
        '<div style="padding: 10px; background: #fee; border: 1px solid #fcc; border-radius: 4px; color: #c00;">Call service is not ready. Please refresh the page or sign in again.</div>';
      return;
    }
    
    console.log('[IntalksAICallRibbon] ✅ Credentials validated, proceeding with React render...');

    // Wrap callback to add logging
    const wrappedCallEvent = (event, data) => {
      // Log to your backend API
      this.logCallEvent(event, data);

      // Call client's callback
      if (this.config.onCallEvent) {
        this.config.onCallEvent(event, data);
      }
    };

    // Create or update root
    if (!this.root) {
      console.log('[IntalksAICallRibbon] Creating React root...');
      try {
        this.root = ReactDOM.createRoot(this.container);
        console.log('[IntalksAICallRibbon] ✅ React root created');
      } catch (error) {
        console.error('[IntalksAICallRibbon] ❌ Failed to create React root:', error);
        throw error;
      }
    } else {
      console.log('[IntalksAICallRibbon] Using existing React root');
    }

    // userId on credentials comes from /api/ribbon/init (already resolved server-side).
    const sdkUserId = String(this.credentials.userId || "").trim();

    if (!this.agentUserId) {
      console.warn('[IntalksAICallRibbon] ⚠️ WARNING: agentUserId not provided!');
      console.warn('[IntalksAICallRibbon] Each agent should have a unique agentUserId for proper Exotel SIP registration.');
      console.warn('[IntalksAICallRibbon] Falling back to server default userId:', sdkUserId);
      console.warn('[IntalksAICallRibbon] Please provide agentUserId in init() config for production use.');
    }

    console.log('[IntalksAICallRibbon] Using Exotel userId:', sdkUserId);
    console.log('[IntalksAICallRibbon] Agent User ID from client:', this.agentUserId || 'Not provided (using server default)');

    const ribbonProps = {
      config: {
        accessToken: this.credentials.exotelToken,
        userId: sdkUserId,
        userMappingVerified: Boolean(this.credentials.exotelUserMappingVerified)
      },
      customerData: this.customerData || {},
      position: this.config.position || 'bottom',
      minimized: this.config.minimized || false,
      onCallEvent: wrappedCallEvent,
      onReady: () => {
        console.log('[IntalksAICallRibbon] ✅ Ribbon component reported ready');
      },
      apiUrl: this.config.apiUrl || window.INTALKSAI_API_URL || 
             (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
               ? 'https://api.intalksai.com' 
               : 'https://api.intalksai.com'),
      apiKey: this.config.apiKey
    };
    
    console.log('[IntalksAICallRibbon] Rendering CallControlRibbon with props:', {
      ...ribbonProps,
      config: { ...ribbonProps.config, accessToken: ribbonProps.config.accessToken?.substring(0, 20) + '...' }
    });
    
    try {
      const ribbonElement = React.createElement(CallControlRibbon, ribbonProps);
      console.log('[IntalksAICallRibbon] React element created, calling root.render()...');
      this.root.render(ribbonElement);
      console.log('[IntalksAICallRibbon] ✅ React render() call completed');
    } catch (error) {
      console.error('[IntalksAICallRibbon] ❌ ERROR during React render:', error);
      console.error('[IntalksAICallRibbon] Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Log call event to backend
   */
  async logCallEvent(event, data) {
    try {
      const apiUrl = this.config.apiUrl || window.INTALKSAI_API_URL || 
                     (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                       ? 'https://api.intalksai.com' 
                       : 'https://api.intalksai.com');
      const response = await fetch(`${apiUrl}/api/ribbon/log-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey: this.config.apiKey,
          event,
          data,
          timestamp: new Date().toISOString(),
          domain: window.location.hostname,
          agentUserId: this.agentUserId || null,
          clientName: this.clientName || null
        })
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        const friendlyMessage = userFacingApiMessage(
          response.status,
          errorPayload,
          'Could not save activity details. Your call can continue.'
        );
        this.emitRibbonNotification(friendlyMessage, 'warning');
      }
    } catch (error) {
      console.error('[IntalksAICallRibbon] Failed to log call event:', error);
      this.emitRibbonNotification('Could not save activity details. Your call can continue.', 'warning');
    }
  }

  emitRibbonNotification(message, type = 'error') {
    const text = normalizeUserFacingMessage(String(message ?? ''));
    if (!text) return;
    try {
      window.dispatchEvent(
        new CustomEvent('intalksai:ribbon-notification', {
          detail: { message: text, type }
        })
      );
    } catch (error) {
      console.warn('[IntalksAICallRibbon] Unable to dispatch ribbon notification:', error);
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    const text = normalizeUserFacingMessage(String(message ?? ''));
    if (!text) return;
    this.emitRibbonNotification(text, 'error');

    let host = document.getElementById('intalksai-ribbon-global-error');
    if (!host) {
      host = document.createElement('div');
      host.id = 'intalksai-ribbon-global-error';
      host.style.position = 'fixed';
      host.style.top = '16px';
      host.style.right = '16px';
      host.style.maxWidth = '420px';
      host.style.background = '#fef2f2';
      host.style.color = '#b91c1c';
      host.style.padding = '12px 14px';
      host.style.border = '1px solid #fecaca';
      host.style.borderRadius = '10px';
      host.style.boxShadow = '0 8px 22px rgba(0,0,0,0.18)';
      host.style.fontFamily = '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif';
      host.style.fontSize = '13px';
      host.style.zIndex = '10002';
      document.body.appendChild(host);
    }

    host.textContent = `⚠️ ${text}`;
    clearTimeout(this._errorHostTimer);
    this._errorHostTimer = setTimeout(() => {
      if (host && host.parentNode) {
        host.parentNode.removeChild(host);
      }
    }, 5000);
  }

  /**
   * Destroy the ribbon and clean up
   */
  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }

    this.config = null;
    this.credentials = null;
    this.customerData = null;
  }
}

// Create global instance
const ribbonInstance = new IntalksAICallRibbonWidget();

// Expose global API - Support both old and new names for compatibility
window.IntalksAICallRibbon = {
  /**
   * Initialize the ribbon
   */
  init: (config) => ribbonInstance.init(config),

  /**
   * Set customer data
   */
  setCustomer: (customerData) => ribbonInstance.setCustomer(customerData),

  /**
   * Change ribbon position
   */
  setPosition: (position) => ribbonInstance.setPosition(position),

  /**
   * Show or hide ribbon
   */
  setVisible: (visible) => ribbonInstance.setVisible(visible),

  /**
   * Minimize or expand ribbon
   */
  setMinimized: (minimized) => ribbonInstance.setMinimized(minimized),

  /**
   * Make a call
   */
  makeCall: (phoneNumber) => ribbonInstance.makeCall(phoneNumber),

  /**
   * Destroy ribbon
   */
  destroy: () => ribbonInstance.destroy(),

  /**
   * Get version
   */
  version: '1.0.0'
};

// Backward compatibility - alias to old name
window.ExotelCallRibbon = window.IntalksAICallRibbon;

// AMD/UMD support
if (typeof define === 'function' && define.amd) {
  define([], function() {
    return window.IntalksAICallRibbon;
  });
}

// CommonJS support
if (typeof module === 'object' && module.exports) {
  module.exports = window.IntalksAICallRibbon;
}

  console.log('[IntalksAI Call Ribbon] Widget loaded v' + window.IntalksAICallRibbon.version);
